<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  client:   { type: Object, default: null },
  errors:   { type: Object, default: () => ({}) },
  save_url: { type: String, default: '' },
  back_url: { type: String, default: '/administration' },
})

const form = useForm({
  nom:       props.client?.nom       || '',
  subdomain: props.client?.subdomain || '',
  schema:    props.client?.schema    || '',
  address:   props.client?.address   || '',
  locale:    props.client?.locale    || 'fr',
})

const { error: toastError } = useToast()

const fieldLabels = {
  nom:       'Nom',
  subdomain: 'Sous-domaine',
  schema:    'Schéma BD',
  address:   'Adresse',
  locale:    'Langue',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}

function submit() {
  form.transform(data => ({ client: data })).post(props.save_url, {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

function cancel() {
  window.location.href = props.back_url
}

const fieldError = (field) => form.errors[field] || props.errors[field]?.[0] || null
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Configuration &rsaquo; Client
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Détails du client
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ client?.nom || 'Aucun client actif' }}
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Sous-navigation -->
        <nav class="flex items-center gap-1 bg-surface-container-low rounded-md p-1 border border-outline-variant/10 mb-6">
          <a href="/administration"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Liste
          </a>
          <a href="/administration/edit_config_client"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-primary bg-surface-container rounded-sm border-l-4 border-tertiary">
            Client
          </a>
          <a href="/administration/edit_logos"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Logos
          </a>
          <a href="/administration/edit_config_bordereau"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Bordereau
          </a>
          <a href="/administration/edit_config_importation"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Importation
          </a>
          <a href="/administration/edit_config_module"
            class="flex-1 text-center px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-outline hover:text-on-surface hover:bg-surface-container/50 rounded-sm transition-all">
            Modules
          </a>
        </nav>

        <!-- Empty state si aucun client -->
        <div v-if="!client" class="bg-surface-container rounded-xl p-12 border-l-4 border-primary shadow-2xl shadow-black/40 text-center">
          <span class="material-symbols-outlined text-5xl text-outline/40 mb-3">domain_disabled</span>
          <p class="text-sm text-on-surface font-bold">Aucun client associé à ce sous-domaine.</p>
          <p class="text-xs text-outline/60 mt-1">Accédez à la plateforme via le sous-domaine d'un client pour modifier sa configuration.</p>
          <a
            href="/clients"
            class="inline-flex items-center gap-2 mt-6 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-sm">list</span>
            Voir la liste des clients
          </a>
        </div>

        <!-- Form Card -->
        <div v-else class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Nom -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Nom <span class="text-error">*</span>
              </label>
              <input
                v-model="form.nom"
                type="text"
                placeholder="Nom du client"
                class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                :class="fieldError('nom') ? 'ring-2 ring-error/40' : ''"
              />
              <p v-if="fieldError('nom')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('nom') }}</p>
            </div>

            <!-- Sous-domaine + Schéma -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Sous-domaine <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.subdomain"
                  type="text"
                  placeholder="ex : monclient"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('subdomain') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('subdomain')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('subdomain') }}</p>
                <p class="mt-1.5 text-[10px] text-outline/60">Utilisé pour l'URL d'accès du client</p>
              </div>
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Schéma BD <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.schema"
                  type="text"
                  placeholder="ex : monclient"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('schema') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('schema')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('schema') }}</p>
                <p class="mt-1.5 text-[10px] text-outline/60">Attention : modifier ce champ peut rendre le tenant inaccessible</p>
              </div>
            </div>

            <!-- Adresse -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Adresse
              </label>
              <textarea
                v-model="form.address"
                rows="2"
                placeholder="Adresse complète"
                class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
              ></textarea>
            </div>

            <!-- Langue -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Langue
                </label>
                <div class="relative">
                  <select
                    v-model="form.locale"
                    class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 appearance-none transition-all outline-none"
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                  </select>
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-outline material-symbols-outlined text-base">expand_more</span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4 pt-6">
              <button
                type="submit"
                :disabled="form.processing"
                class="flex-1 machined-sheen text-on-primary font-black py-4 rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">save</span>
                Enregistrer
              </button>
              <button
                type="button"
                @click="cancel"
                class="px-8 py-4 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </button>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
